import {
  OPERATOR_TYPES_1,
  OPERATOR_TYPES_3,
  OPERATOR_TYPES_4,
} from 'dashboard/routes/dashboard/settings/automation/operators';
import {
  CALL_DIRECTION_CONDITION_VALUES,
  CALL_STATUS_CONDITION_VALUES,
  CALL_STATUS_REASON_CONDITION_VALUES,
} from 'dashboard/routes/dashboard/settings/automation/constants';

import filterQueryGenerator from './filterQueryGenerator';
import actionQueryGenerator from './actionQueryGenerator';
import inboxes from '../api/inboxes';
import { template } from 'babel-core';
const MESSAGE_CONDITION_VALUES = [
  {
    id: 'incoming',
    name: 'Incoming Message',
  },
  {
    id: 'outgoing',
    name: 'Outgoing Message',
  },
];

export const PRIORITY_CONDITION_VALUES = [
  {
    id: 'nil',
    name: 'None',
  },
  {
    id: 'low',
    name: 'Low',
  },
  {
    id: 'medium',
    name: 'Medium',
  },
  {
    id: 'high',
    name: 'High',
  },
  {
    id: 'urgent',
    name: 'Urgent',
  },
];

export const getCustomAttributeInputType = key => {
  const customAttributeMap = {
    date: 'date',
    text: 'plain_text',
    list: 'search_select',
    checkbox: 'search_select',
  };

  return customAttributeMap[key] || 'plain_text';
};

export const isACustomAttribute = (customAttributes, key) => {
  return customAttributes.find(attr => {
    return attr.attribute_key === key;
  });
};

export const getCustomAttributeListDropdownValues = (
  customAttributes,
  type
) => {
  return customAttributes
    .find(attr => attr.attribute_key === type)
    .attribute_values.map(item => {
      return {
        id: item,
        name: item,
      };
    });
};

export const isCustomAttributeCheckbox = (customAttributes, key) => {
  return customAttributes.find(attr => {
    return (
      attr.attribute_key === key && attr.attribute_display_type === 'checkbox'
    );
  });
};

export const isCustomAttributeList = (customAttributes, type) => {
  return customAttributes.find(attr => {
    return (
      attr.attribute_key === type && attr.attribute_display_type === 'list'
    );
  });
};

export const getOperatorTypes = key => {
  const operatorMap = {
    list: OPERATOR_TYPES_1,
    text: OPERATOR_TYPES_3,
    number: OPERATOR_TYPES_1,
    link: OPERATOR_TYPES_1,
    date: OPERATOR_TYPES_4,
    checkbox: OPERATOR_TYPES_1,
  };

  return operatorMap[key] || OPERATOR_TYPES_1;
};

export const generateCustomAttributeTypes = (customAttributes, type) => {
  return customAttributes.map(attr => {
    return {
      key: attr.attribute_key,
      name: attr.attribute_display_name,
      inputType: getCustomAttributeInputType(attr.attribute_display_type),
      filterOperators: getOperatorTypes(attr.attribute_display_type),
      customAttributeType: type,
    };
  });
};

export const generateConditionOptions = (options, key = 'id') => {
  return options.map(i => {
    return {
      id: i[key],
      name: i.title,
    };
  });
};


export const generateConditionOptionsInboxes = (options, key = 'id') => {
  return options?.map(i => {
    return {
      id: i[key],
      name: i.title,
    };
  });
};

// Add the "None" option to the agent list
export const agentList = agents => [
  {
    id: 'nil',
    name: 'From Agent List',
  },
  ...(agents || []),
];

export const getActionOptions = ({ agents, teams, labels, type, inboxes, boards , macros}) => {

  const actionsMap = {
    assign_agent: agentList(agents),
    assign_team: teams,
    send_email_to_team: teams,
    add_contact_label: generateConditionOptions(labels, 'title'),
    add_conversation_label: generateConditionOptions(labels, 'title'),
    remove_contact_label: generateConditionOptions(labels, 'title'),
    remove_conversation_label: generateConditionOptions(labels, 'title'),
    change_priority: PRIORITY_CONDITION_VALUES,
    whatsapp_template: generateConditionOptionsInboxes(inboxes, 'title'),
    add_board: boards,
    add_macro: macros,
  };
  return actionsMap[type];
};

export const getConditionOptions = ({
  agents,
  booleanFilterOptions,
  campaigns,
  contacts,
  countries,
  customAttributes,
  inboxes,
  labels,
  languages,
  statusFilterOptions,
  teams,
  type,
  boards,
}) => {
  if (isCustomAttributeCheckbox(customAttributes, type)) {
    return booleanFilterOptions;
  }

  if (isCustomAttributeList(customAttributes, type)) {
    return getCustomAttributeListDropdownValues(customAttributes, type);
  }

  const conditionFilterMaps = {
    status: statusFilterOptions,
    assignee_id: agents,
    contact: contacts,
    inbox_id: inboxes,
    team_id: teams,
    campaigns: generateConditionOptions(campaigns),
    browser_language: languages,
    conversation_language: languages,
    country_code: countries,
    message_type: MESSAGE_CONDITION_VALUES,
    priority: PRIORITY_CONDITION_VALUES,
    board_id: boards,
    conversation_labels: generateConditionOptions(labels, 'title'),
    call_status: CALL_STATUS_CONDITION_VALUES,
    call_direction: CALL_DIRECTION_CONDITION_VALUES,
    call_status_reason: CALL_STATUS_REASON_CONDITION_VALUES,
  };

  return conditionFilterMaps[type];
};

export const normalizeAiEmailAttachmentBlobIds = params => {
  if (Array.isArray(params?.attachment_blob_ids)) {
    return params.attachment_blob_ids.filter(Boolean);
  }
  if (params?.attachment_blob_id) {
    return [params.attachment_blob_id];
  }
  return [];
};

export const getAiEmailAttachments = (action, files = []) => {
  const params = Array.isArray(action.action_params)
    ? action.action_params[0]
    : action.action_params;
  return normalizeAiEmailAttachmentBlobIds(params).map(blobId => {
    const file = files.find(item => item.blob_id === blobId);
    return {
      blobId,
      filename: file ? file.filename.toString() : '',
    };
  });
};

export const getFileName = (action, files = []) => {
  if (action.action_name === 'send_ai_generated_email') {
    const attachments = getAiEmailAttachments(action, files);
    if (!attachments.length) return '';
    return attachments.map(attachment => attachment.filename).filter(Boolean).join(', ');
  }

  const blobId = action.action_params[0];
  if (!blobId) return '';
  if (action.action_name === 'send_attachment') {
    const file = files.find(item => item.blob_id === blobId);
    if (file) return file.filename.toString();
  }
  return '';
};

export const getDefaultConditions = eventName => {
  if (eventName === 'message_created') {
    return [
      {
        attribute_key: 'message_type',
        filter_operator: 'equal_to',
        values: '',
        query_operator: 'and',
        custom_attribute_type: '',
      },
    ];
  }
  if (eventName === 'conversation_opened') {
    return [
      {
        attribute_key: 'browser_language',
        filter_operator: 'equal_to',
        values: '',
        query_operator: 'and',
        custom_attribute_type: '',
      },
    ];
  }

  if (eventName === 'board_status_change') {
    return [
      {
        attribute_key: 'status',
        filter_operator: 'equal_to',
        values: '',
        query_operator: 'and',
        custom_attribute_type: '',
      },
    ];
  }

  if (eventName === 'lead_quality_score_generated') {
    return [
      {
        attribute_key: 'lead_quality_score',
        filter_operator: 'is_greater_than',
        values: '1',
        query_operator: 'and',
        custom_attribute_type: '',
      },
    ];
  }

  if (eventName === 'contact_joined_meeting') {
    return [
      {
        attribute_key: 'meeting_present',
        filter_operator: 'is_present',
        values: 'yes',
        query_operator: 'and',
        custom_attribute_type: '',
      },
    ];
  }

  if (eventName === 'whatsapp_flow_submitted') {
    return [
      {
        attribute_key: 'whatsapp_flow_id',
        filter_operator: 'equal_to',
        values: '',
        query_operator: 'and',
        custom_attribute_type: '',
      },
    ];
  }

  if (eventName === 'call_ended') {
    const missed = CALL_STATUS_CONDITION_VALUES.find(
      option => option.id === 'missed'
    );
    return [
      {
        attribute_key: 'call_status',
        filter_operator: 'equal_to',
        values: missed ? [missed] : [],
        query_operator: 'and',
        custom_attribute_type: '',
      },
    ];
  }

  return [
    {
      attribute_key: 'status',
      filter_operator: 'equal_to',
      values: '',
      query_operator: 'and',
      custom_attribute_type: '',
    },
  ];
};

export const getDefaultActions = (eventName = '') => {
  if (eventName === 'call_ended') {
    return [
      {
        action_name: 'add_macro',
        action_params: [],
        delay_minutes: '',
      },
    ];
  }

  return [
    {
      action_name: 'assign_agent',
      action_params: [],
    },
  ];
};

export const filterCustomAttributes = customAttributes => {
  return customAttributes.map(attr => {
    return {
      key: attr.attribute_key,
      name: attr.attribute_display_name,
      type: attr.attribute_display_type,
    };
  });
};

export const getStandardAttributeInputType = (automationTypes, event, key) => {
  return automationTypes[event].conditions.find(item => item.key === key)
    .inputType;
};

export const normalizeSavedOptionIds = params => {
  if (params === undefined || params === null) return [];
  const list = Array.isArray(params) ? params : [params];
  return list
    .map(entry => {
      if (entry && typeof entry === 'object') {
        return entry.id ?? entry.macro_id ?? entry.board_id ?? entry.team_id;
      }
      return entry;
    })
    .filter(id => id !== undefined && id !== null && id !== '');
};

export const matchOptionsByIds = (options, params) => {
  const savedIds = normalizeSavedOptionIds(params);
  return (options || []).filter(item =>
    savedIds.some(savedId => String(savedId) === String(item.id))
  );
};

export const CALL_MULTI_SELECT_CONDITION_KEYS = [
  'call_status',
  'call_direction',
  'call_status_reason',
];

const CALL_CONDITION_OPTIONS = {
  call_status: CALL_STATUS_CONDITION_VALUES,
  call_direction: CALL_DIRECTION_CONDITION_VALUES,
  call_status_reason: CALL_STATUS_REASON_CONDITION_VALUES,
};

export const resolveMultiSelectConditionValues = (attributeKey, values) => {
  if (!CALL_MULTI_SELECT_CONDITION_KEYS.includes(attributeKey)) {
    return values;
  }

  const options = CALL_CONDITION_OPTIONS[attributeKey] || [];
  if (!values || values === '') {
    return [];
  }

  return matchOptionsByIds(options, values);
};

export const generateAutomationPayload = payload => {
  const automation = JSON.parse(JSON.stringify(payload));
  automation.conditions[automation.conditions.length - 1].query_operator = null;
  automation.conditions = filterQueryGenerator(automation.conditions).payload;
  automation.actions = actionQueryGenerator(automation.actions);
  return automation;
};

export const isCustomAttribute = (attrs, key) => {
  return attrs.find(attr => attr.key === key);
};

export const generateCustomAttributes = (
  conversationAttributes = [],
  contactAttributes = [],
  conversationlabel,
  contactlabel
) => {
  const customAttributes = [];
  if (conversationAttributes.length) {
    customAttributes.push(
      {
        key: `conversation_custom_attribute`,
        name: conversationlabel,
        disabled: true,
      },
      ...conversationAttributes
    );
  }
  if (contactAttributes.length) {
    customAttributes.push(
      {
        key: `contact_custom_attribute`,
        name: contactlabel,
        disabled: true,
      },
      ...contactAttributes
    );
  }
  return customAttributes;
};
